/**
 * Copy of the Review AMS Data page (`components/Ams/review/`). VentaHub's
 * English, verbatim where VentaHub had it; each block cites the production
 * file it comes from (`pages/review_ams_data/…`).
 */
export const reviewAms = {
  // ReviewAmsDataPage.tsx:395-397
  page: {
    title: 'Review AMS Data',
    description:
      'Period-over-period AMS performance review across ad groups, ASINs, targets, and search terms.',
  },
  // ReviewAmsDataPage.tsx:358-454
  export: {
    button: 'Export CSV',
    pending: 'Exporting…',
    aggregated: 'CSV — aggregated',
    byAdGroup: 'CSV — by ad group',
    adGroupTab: 'Ad Groups tab is already per ad group',
    altProductTab: 'Not available for Alt Products',
    failed: 'Export failed — please try again.',
  },
  // ReviewAmsDataPage.tsx:459-579
  filters: {
    label: 'Review filters',
    currentPeriod: 'Current period',
    previousPeriod: 'Previous period',
    adType: 'Ad Type',
    adTypeAll: 'All',
    // LEVEL_TABS[].searchLabel (ReviewAmsDataPage.tsx:78-86)
    search: {
      ad_group: 'Search ad group / campaign',
      ad_product: 'Search ASIN / SKU',
      target: 'Search target',
      search_term: 'Search term',
      negative: 'Search negative keyword',
      alt_product: 'Search purchased ASIN',
    },
    go: 'Go',
    upToDate: 'Up to date',
  },
  // components/ComparisonSummary.tsx
  summary: {
    title: 'Period Comparison',
    // "9 Oct – 15 Oct vs 2 Oct – 8 Oct" (ReviewAmsDataPage.tsx:96-97, 350-352)
    range: '{{current}} vs {{previous}}',
    period: '{{from}} – {{to}}',
    adGroups_one: '{{formatted}} ad group',
    adGroups_other: '{{formatted}} ad groups',
    expand: 'Expand summary',
    collapse: 'Collapse summary',
    tooltip: '{{description}} Previous: {{previous}}',
    tooltipChange: '{{description}} Previous: {{previous}} · Change: {{change}}',
    loadFailed: 'Failed to load the period comparison: {{message}}',
  },
  // ReviewAmsDataPage.tsx:587-643
  tabs: {
    label: 'Review level',
    ad_group: 'Ad Groups',
    ad_product: 'ASINs',
    target: 'Targets',
    search_term: 'Search Terms',
    negative: 'Negatives',
    alt_product: 'Alt Products',
    drill: 'Ad group: {{name}}',
    clearDrill: 'Show every ad group again',
    altCaption:
      'Purchases of <em>other</em> products than the one advertised — the ad’s halo / cross-sell surface. The purchased-product report carries only units and sales, so the metric set here is reduced.',
  },
  // components/reviewMetrics.tsx:23-88
  metrics: {
    impressions: { label: 'Impr.', description: 'Times the ad was shown in the period.' },
    clicks: { label: 'Clicks', description: 'Times shoppers clicked the ad.' },
    ctr: { label: 'CTR', description: 'Click-through rate — clicks ÷ impressions × 100.' },
    cpc: { label: 'CPC', description: 'Average cost per click — spend ÷ clicks.' },
    units: { label: 'Units', description: 'Units sold attributed to the ads.' },
    conversion: {
      label: 'Conv.',
      description:
        'Conversion rate — units ÷ clicks × 100. Units, not orders: the marketing tables carry no order count at these grains.',
    },
    sales: { label: 'Sales', description: 'Sales revenue attributed to the ads.' },
    asp: {
      label: 'ASP',
      description:
        'Average selling price — sales ÷ units. Dash when the period sold no units (the ratio has no denominator).',
    },
    spend: { label: 'Spend', description: 'Total ad spend in the period.' },
    acos: {
      label: 'ACOS',
      description: 'Advertising cost of sales — spend ÷ sales × 100. Lower is better.',
    },
  },
  // DeltaPill (components/reviewMetrics.tsx:153-203)
  pill: {
    new: 'new',
    none: '—',
    flat: '0%',
    up: '▲{{value}}%',
    down: '▼{{value}}%',
    capped: '>999',
  },
  // MetricCellContent (components/reviewMetrics.tsx:221-264)
  cell: {
    previous: 'Previous: {{previous}}',
    previousChange: 'Previous: {{previous}} · Change: {{change}}',
    target: '{{text}} · Target: {{target}}',
  },
  // formatBidRange (components/reviewMetrics.tsx:278-291)
  bidRange: '{{low}} – {{high}}',
  // components/ReviewTable.tsx
  table: {
    expand: 'Show the review of {{name}}',
    collapse: 'Hide the review of {{name}}',
    expandColumn: 'Review',
    // LEVEL_ENTITY_HEADER (:80-87)
    entity: {
      ad_group: 'Ad Group',
      ad_product: 'ASIN',
      target: 'Target',
      search_term: 'Search Term',
      alt_product: 'Purchased ASIN',
    },
    // entityNoun (:263): the header lower-cased and pluralised — "asins", as VentaHub wrote it.
    noun: {
      ad_group_one: 'ad group',
      ad_group_other: 'ad groups',
      ad_product_one: 'asin',
      ad_product_other: 'asins',
      target_one: 'target',
      target_other: 'targets',
      search_term_one: 'search term',
      search_term_other: 'search terms',
      alt_product_one: 'purchased asin',
      alt_product_other: 'purchased asins',
    },
    count: '{{formatted}} {{noun}}',
    state: 'State',
    keyword: 'Keyword',
    keywordHint:
      'The targets / keywords whose ads this search term matched, from the same search-term report rows. A term matched by several keywords lists each one; an ASIN target links to its product page, a keyword to its Amazon search.',
    bid: 'Bid',
    bidHint:
      'Current bid per ad group — the live value from Amazon, falling back to the latest bid on the daily report when the target isn’t mirrored yet. A row covering several ad groups shows the range across them. Measured over both periods, so it stays populated for a target that only ran in the previous one — which is why it can span more ad groups than the Active in list below.',
    empty: 'No {{noun}} with AMS data in the selected periods.',
    notApplied: 'Select a store and press Go to load the review.',
    rowsPerPage: 'Rows per page',
    from: 'from {{asins}}',
  },
  // ReviewPanel (components/ReviewTable.tsx:549-822)
  panel: {
    verdict: {
      noActivity: 'No activity in either period',
      beating: 'Beating ACOS target by {{value}}%',
      over: 'Over ACOS target by {{value}}%',
      newThisPeriod: 'New this period — no previous-period activity',
      spendUpSalesFell: 'Spend up {{spend}}% while sales fell {{sales}}%',
      salesUpLessSpend: 'Sales up {{sales}}% on {{spend}}% less spend',
      bothUp: 'Spend up {{spend}}%, sales up {{sales}}%',
      bothDown: 'Spend down {{spend}}%, sales down {{sales}}%',
    },
    spec: {
      type: 'Type',
      campaign: 'Campaign',
      targetAcos: 'Target ACOS',
      targetConversion: 'Target conv.',
      currentConversion: 'Current conv.',
      targetAsp: 'Target ASP',
      adGroupId: 'Ad group ID',
      skuCodes: 'SKU codes',
      matchType: 'Match type',
      bid: 'Bid',
      matchTypes: 'Match types',
      activeIn: 'Active in',
      activeInCount: 'Active in ({{count}})',
    },
    crossDrill: {
      label: 'Review this ad group by level',
      ad_product: 'Review ASINs',
      target: 'Review Targets',
      search_term: 'Review Search Terms',
    },
  },
  // ComparisonGrid (components/ReviewTable.tsx:828-930)
  grid: {
    label: 'Previous and current period of {{name}}',
    metric: 'Metric',
    previous: 'Previous',
    current: 'Current',
    change: 'Change',
    changePct: 'Change %',
    new: 'new',
    pct: '{{value}}%',
  },
  // components/NegativesTable.tsx
  negatives: {
    info: 'Negatives are configuration — they block matching, so no performance metrics exist for them. This is the latest snapshot per negative.',
    columns: {
      keyword_text: 'Negative Keyword/Target',
      match_type: 'Match Type',
      negative_type: 'Type',
      status: 'Status',
      ad_group_count: 'Ad Groups',
      latest_date: 'Last Snapshot',
    },
    // formatNegativeType (:54-59)
    type: { keyword: 'Keyword', target: 'Target' },
    empty: 'No negatives found for the selected filters.',
    count_one: '{{formatted}} negative',
    count_other: '{{formatted}} negatives',
  },
} as const
